import * as React from 'react';
import { Table } from 'react-bootstrap';
import Axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import {
  formatActionTaken,
  apptHistoryUserName,
  formatExactTimestamp,
} from 'utils';
import {
  EnhancedAppointment,
  IAppointmentConfig,
  IAppointmentEmailDelivery,
  IAppointmentTextLog,
  IOfferAction,
  IOfferEmailDelivery,
  OfferActionType,
  AppointmentConfigDays,
  AppointmentConfigTimes,
  AppointmentHistoryEntry,
  BuildJsonType,
  OfferActionContactType,
  Timezone,
} from '@bb/domain/dist/types';

import errorReporter from '@app/errorReporter';
import Loading from './Loading';
import DeliveryLog from './DeliveryLog';
import Pagination from './Pagination';

type ActionTabType = 'appointment' | 'offer';
type ActionTabName =
  | 'contacts'
  | 'history'
  | 'emails'
  | 'sms'
  | 'restrictions'
  | 'all';
type RestrictionType = 'weekly' | 'special';

type ActionTabsState = {
  loading: boolean;
  tab: ActionTabName;
  restrictions: RestrictionType;
  restrictDate?: string;
  emailDeliveryLog: IAppointmentEmailDelivery[];
  smsDeliveryLog: IAppointmentTextLog[];
  offerEmailDelivery: IOfferEmailDelivery[];
  offerHistory: IOfferAction[];
  count: number;
  page: number;
};

type ActionTabsProps = {
  type: ActionTabType;
  appointment?: BuildJsonType<EnhancedAppointment>;
  offerId?: string;
  listingId?: string;
  appointmentConfig?: IAppointmentConfig;
  timezone?: Timezone;
};

const TABS = {
  offer: ['history'],
  appointment: ['contacts', 'history', 'emails', 'sms', 'restrictions', 'all'],
};
const RESTRICTION_MAP = {
  weekly: { header: 'Weekly' },
  special: { header: 'Special Day' },
};
const TAB_MAP = {
  contacts: { header: 'Listing Contacts' },
  history: { header: 'History' },
  emails: { header: 'Email Delivery Log' },
  sms: { header: 'SMS Delivery Log' },
  restrictions: { header: 'Restrictions' },
  all: { header: 'All Appointments' },
};

const OFFER_ACTION_MAP: Record<OfferActionType, string> = {
  created: 'Offer Created',
  deleted: 'Offer Deleted',
  reactivated: 'Offer Reactivated',
  formUploaded: 'Offer Doc Uploaded',
  formDeleted: 'Offer Doc Deleted',
  formUpdated: 'Offer Doc Updated',
  formVerified: 'Offer Doc Verified',
  notifiedAll: 'Notified All Showing Agents',
  notifiedRegistered: 'Notified Agents with Registered Offer',
  notifiedListingAgent: 'Notified Listing Agent to Take Action',
  notifiedSelected: 'Notified Selected Agents',
};

class ActionTabs extends React.Component<ActionTabsProps, ActionTabsState> {
  constructor(props: ActionTabsProps) {
    super(props);
    this.state = {
      loading: true,
      tab: 'history',
      restrictions: 'weekly',
      restrictDate: undefined,
      emailDeliveryLog: [],
      smsDeliveryLog: [],
      offerEmailDelivery: [],
      offerHistory: [],
      count: 0,
      page: 1,
    };
  }

  public componentDidMount() {
    this.fetchData();
  }

  public fetchData = async () => {
    try {
      const { type } = this.props;
      if (type === 'appointment') {
        const { emailDeliveryLog, smsDeliveryLog } =
          await this.fetchAppointmentDeliveries();
        this.setState({ emailDeliveryLog, smsDeliveryLog });
      } else {
        const { count, offerHistory } = await this.fetchOfferHistory();
        const offerEmailDelivery = await this.fetchOfferDeliveries();
        this.setState({ count, offerHistory, offerEmailDelivery });
      }
    } catch (err) {
      errorReporter.error(err);
    } finally {
      this.setState({ loading: false });
    }
  };

  public fetchOfferHistory = async () => {
    try {
      const { page } = this.state;
      const { offerId, listingId } = this.props;

      const {
        data: { count, actions: offerHistory },
      } = await Axios.get<{
        count: number;
        actions: IOfferAction[];
      }>(`/listing/${listingId}/offer/${offerId}/actions`, {
        params: { page },
      });
      return { count, offerHistory };
    } catch (err) {
      errorReporter.error(`Error fetching offer history: ${err}`);
      return { count: 0, offerHistory: [] };
    }
  };

  public fetchOfferDeliveries = async () => {
    const { offerId, listingId } = this.props;
    try {
      const { data: offerEmailDelivery } = await Axios.get<
        IOfferEmailDelivery[]
      >(`/listing/${listingId}/offer/${offerId}/delivery`);
      return offerEmailDelivery;
    } catch (err) {
      errorReporter.error(`Error fetching offer email delivery logs: ${err}`);
    }
  };

  public fetchAppointmentDeliveries = async () => {
    try {
      const { appointment } = this.props;

      const [{ data: emailDeliveryLog }, { data: smsDeliveryLog }] =
        await Promise.all([
          Axios.get<IAppointmentEmailDelivery[]>(
            `/appointment/${appointment.id}/delivery`,
          ),
          Axios.get<IAppointmentTextLog[]>(
            `/appointment/${appointment.id}/texts`,
          ),
        ]);

      return { emailDeliveryLog, smsDeliveryLog };
    } catch (err) {
      errorReporter.error(`Error fetching appointment delivery logs: ${err}`);
    }
  };

  public formatStatus = (status: string) => {
    if (!status) return status;
    return status.replace('_', ' ').replace(/(^| )(\w)/g, x => x.toUpperCase());
  };

  public selectSpecialDay = (day: string) => {
    this.setState({ restrictDate: day });
  };

  public changeTab = (tab: ActionTabName) => {
    this.setState({ tab });
  };

  public changeRestriction = (restrictions: RestrictionType) => {
    this.setState({ restrictions });
  };

  public onPageChange = (page: number) => {
    this.setState({ page }, async () => {
      const { count, offerHistory } = await this.fetchOfferHistory();
      this.setState({ count, offerHistory });
    });
  };

  public formatActionTaken = (
    historyEntry: BuildJsonType<AppointmentHistoryEntry>,
  ) => {
    const { appointment, timezone } = this.props;
    return formatActionTaken(
      historyEntry,
      appointment?.type,
      appointment?.coop,
      undefined,
      appointment?.history,
      timezone,
    );
  };

  private renderUserLabel = (contactType?: OfferActionContactType) => {
    if (contactType === 'answeringuser') return 'ANSWERING SERVICE - ';
    if (contactType === 'mlsstaff') return 'MLS STAFF - ';
    return '';
  };

  render() {
    const {
      tab: currentTab,
      restrictions,
      restrictDate,
      loading,
      emailDeliveryLog,
      smsDeliveryLog,
      offerEmailDelivery,
      offerHistory,
      count,
      page,
    } = this.state;
    const { appointmentConfig, type, appointment, timezone } = this.props;

    return loading ? (
      <Loading size={'fa-3x'} />
    ) : (
      <div className="row appointment-margin-top appointment-details">
        <div className="col-md-12">
          <ul className="nav nav-tabs">
            {TABS[type].map((tab: ActionTabName) => (
              <li className={currentTab === tab ? 'active' : ''} key={uuidv4()}>
                <a onClick={() => this.changeTab(tab)}>{TAB_MAP[tab].header}</a>
              </li>
            ))}
          </ul>
          {currentTab === 'history' && type === 'appointment' && (
            <div>
              {!appointment.history?.length ? (
                <h5>No history recorded for this {type}.</h5>
              ) : (
                <Table className="table" striped bordered>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>User</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {appointment.history
                      .sort(
                        (a, b) =>
                          new Date(b.date).getTime() -
                          new Date(a.date).getTime(),
                      )
                      .map(h => (
                        <tr key={uuidv4()}>
                          <td>
                            {moment(h.date).format('MMM D, YYYY h:mm:ss a')}
                          </td>
                          <td>{apptHistoryUserName(h)}</td>
                          <td>
                            {this.formatStatus(h.status)}{' '}
                            {type === 'appointment' && h.isPending && (
                              <span>
                                (Pending{h.conciergePending ? ' Concierge' : ''}
                                )
                              </span>
                            )}
                          </td>
                          <td>
                            {this.formatActionTaken(h)}
                            {(
                              h.actionDetails as Exclude<
                                (typeof h)['actionDetails'],
                                string
                              >
                            )?.cancelledBy && (
                              <div>
                                {h.status === 'cancel' ? 'Cancelled' : 'Denied'}{' '}
                                {(
                                  h.actionDetails as Exclude<
                                    (typeof h)['actionDetails'],
                                    string | undefined
                                  >
                                ).cancelledBy === 'unable'
                                  ? '-'
                                  : 'by'}{' '}
                                {
                                  (
                                    h.actionDetails as Exclude<
                                      (typeof h)['actionDetails'],
                                      string | undefined
                                    >
                                  ).cancelledBy
                                }
                                {(
                                  h.actionDetails as Exclude<
                                    (typeof h)['actionDetails'],
                                    string | undefined
                                  >
                                ).reason && (
                                  <span>
                                    :{' '}
                                    {
                                      (
                                        h.actionDetails as Exclude<
                                          (typeof h)['actionDetails'],
                                          string | undefined
                                        >
                                      ).reason
                                    }
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </Table>
              )}
            </div>
          )}

          {currentTab === 'history' && type === 'offer' && (
            <div>
              {!offerHistory.length ? (
                <h5>No history recorded for this {type}.</h5>
              ) : (
                <>
                  <Table striped bordered className="table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>User</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    {/* Sorted in backend, ActionService.getActions */}
                    <tbody>
                      {offerHistory.map(h => (
                        <tr key={uuidv4()}>
                          <td>{formatExactTimestamp(h.timestamp, timezone)}</td>
                          <td>
                            {this.renderUserLabel(h.contactType)}
                            {h.userName}
                          </td>
                          <td>
                            {OFFER_ACTION_MAP[h.action]}
                            {h.instructions && `: ${h.instructions}`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                    id="offer-history-paginate"
                  >
                    <Pagination
                      totalItems={count}
                      itemsPerPage={10}
                      maxSize={5}
                      currentPage={page}
                      onChange={this.onPageChange}
                      disabled={loading}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {currentTab === 'emails' && (
            <div style={{ marginBottom: '20px' }}>
              <DeliveryLog
                type="email"
                deliveryLog={
                  type === 'appointment' ? emailDeliveryLog : offerEmailDelivery
                }
                dataType={type}
              />
            </div>
          )}

          {currentTab === 'sms' && (
            <div style={{ marginBottom: '20px' }}>
              <DeliveryLog
                type="sms"
                deliveryLog={smsDeliveryLog}
                dataType={type}
              />
            </div>
          )}

          {currentTab === 'restrictions' && (
            <div>
              <ul className="nav nav-tabs">
                {Object.keys(RESTRICTION_MAP).map((res: RestrictionType) => (
                  <li
                    className={restrictions === res ? 'active' : ''}
                    key={uuidv4()}
                  >
                    <a onClick={() => this.changeRestriction(res)}>
                      {RESTRICTION_MAP[res].header}
                      <br />
                      Restrictions
                    </a>
                  </li>
                ))}
              </ul>

              {restrictions === 'weekly' && (
                <div className="row">
                  <div className="col-md-2"></div>
                  {Object.keys(appointmentConfig.days).map(
                    (day: AppointmentConfigDays) => (
                      <div className="col-md-1" key={uuidv4()}>
                        <h5 style={{ textAlign: 'center' }}>{day}</h5>
                        {Object.keys(appointmentConfig.days[day]).map(
                          (time: AppointmentConfigTimes) => (
                            <div style={{ marginTop: '2px' }} key={uuidv4()}>
                              <a
                                className={`btn btn-block ${
                                  appointmentConfig.days[day][time] === false ||
                                  appointmentConfig.days[day][time] ===
                                    'Restricted'
                                    ? 'btn-default'
                                    : 'btn-success'
                                }`}
                              >
                                {time}
                              </a>
                            </div>
                          ),
                        )}
                      </div>
                    ),
                  )}
                </div>
              )}

              {restrictions === 'special' && (
                <div className="row">
                  <div className="col-md-2">
                    <h5>Special Days</h5>
                    {Object.keys(appointmentConfig.specialTimes).map(day => (
                      <div key={uuidv4()}>
                        <a onClick={() => this.selectSpecialDay(day)}>{day}</a>
                      </div>
                    ))}
                  </div>
                  {!!restrictDate && (
                    <div className="col-md-2 col-md-offset-1">
                      <h5 style={{ textAlign: 'center' }}>{restrictDate}</h5>
                      {Object.keys(
                        appointmentConfig.specialTimes[restrictDate],
                      ).map(time => (
                        <div style={{ marginTop: '2px' }} key={uuidv4()}>
                          <a
                            className={`btn btn-block ${
                              appointmentConfig.specialTimes[restrictDate][time]
                                ? 'btn-success'
                                : 'btn-default'
                            }`}
                          >
                            {time}
                          </a>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {currentTab === 'all' && (
            <div>
              {/* TODO: */}
              {/* <appointment-paginate
                view-appointment="viewDashAppointment"
                listing-id="listing.id"
                limit="8"
              ></appointment-paginate> */}
            </div>
          )}

          {currentTab === 'contacts' && (
            <div>
              {/* TODO: */}
              {/* <div ng-include="'/listing_contacts.html'"></div> */}
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ActionTabs;
